import React, { useEffect } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { formatQueryParamValue, useQueryParams } from "common/hooks/useQueryParams";
import { LP_PORTAL_BASE_PATH, getManagerNameFromPath } from "common/helpers/url";
import { toDbId } from "common/helpers/globalId";
import { isRedirectAllowed } from "apps/common/authentication/common/utils";
import { oxfordComma } from "common/utils/stringUtils";
import { useAppContext } from "apps/portal/Portal/common/AppContext";
import { useIdentifyArena } from "@jsq/flags-react";
import { Login, UserContext } from "apps/common/authentication";
import {
  useGetUnauthenticatedArenaQuery,
  useGetArenaTermsAndPpDetailsQuery,
} from "common/types/mainHooks";
import { LoginLayout } from "common/components/LoginLayout";
import { QueryLoader } from "@jsq/ds/core";
import { type UnauthenticatedArenaObjectType } from "../../types";

type Props = {
  onSuccess: () => void;
  previewMode?: boolean;
  previewArena?: UnauthenticatedArenaObjectType;
};

type LocationState = {
  passwordWasReset: boolean;
};

type UrlParams = {
  path: string;
  activation: string;
  hasActiveGlobal: string;
  hasActiveManagerAccount: string;
  secureDocumentsKey: string;
};

export const LoginPage: React.FunctionComponent<Props> = ({
  onSuccess,
  previewMode = false,
  previewArena,
}) => {
  const { user } = useAppContext();
  const location = useLocation<LocationState>();
  const [params] = useQueryParams();
  const {
    path,
    activation: activationCode,
    hasActiveGlobal: hasActiveGlobalUrlParam,
    hasActiveManagerAccount: hasActiveManagerAccountUrlParam,
    secureDocumentsKey,
  } = params as UrlParams;
  const identifyArena = useIdentifyArena();

  const managerName = getManagerNameFromPath(path);
  const response = useGetUnauthenticatedArenaQuery({
    variables: {
      arenaDomain: managerName,
    },
    context: { unauthenticated: true },
    skip: managerName === "" || previewMode,
  });

  const unauthenticatedArena = response?.data?.unauthenticatedArena || previewArena;
  const photoUrl = unauthenticatedArena?.signInPhotoUrl;

  useEffect(() => {
    if (unauthenticatedArena?.id) {
      const arenaId = toDbId(unauthenticatedArena.id);
      identifyArena(arenaId);
    }
  }, [identifyArena, unauthenticatedArena?.id]);

  const unifiedPortalDetailsQuery = useGetArenaTermsAndPpDetailsQuery({
    variables: { code: activationCode },
    context: { unauthenticated: true },
    skip: !activationCode,
  });
  const queryType = unifiedPortalDetailsQuery.data?.unifiedPortalActivationDetails.__typename;

  /**
   * the presence of the following two url params indicate that the user has been redirected to login
   * from the portal 2.0 activation flow, either because they already have an active global account or
   * because they have an existing staff account with a GP (or both)
   */
  const hasActiveGlobalUser = hasActiveGlobalUrlParam === "t";
  const hasActiveManagerAccount = hasActiveManagerAccountUrlParam === "t";

  const getPrepopulatedEmail = () => {
    const userEmail =
      queryType === "UnifiedPortalActivationDetails"
        ? unifiedPortalDetailsQuery.data?.unifiedPortalActivationDetails?.user?.emailAddress
        : "";
    return userEmail && hasActiveManagerAccount && !user ? userEmail : "";
  };

  // get formatted string of linked GP arena names if available
  const linkedGpArenaNames = oxfordComma(
    queryType === "UnifiedPortalActivationDetails"
      ? unifiedPortalDetailsQuery.data?.unifiedPortalActivationDetails.linkedGpArenaNames || []
      : [],
  );

  // determine whether to display GP's tou/pp checkbox on portal login page
  const gpArena =
    queryType === "UnifiedPortalActivationDetails"
      ? unifiedPortalDetailsQuery.data?.unifiedPortalActivationDetails.gpArena
      : null;
  const arenaHasTouOrPrivacyPolicy = Boolean(
    gpArena?.clientTos || gpArena?.clientPrivacyPolicy,
  );
  const showPortalTouCheckBox =
    Boolean(activationCode) &&
    arenaHasTouOrPrivacyPolicy &&
    (hasActiveManagerAccount ||
      (queryType === "UnifiedPortalActivationDetails" &&
        unifiedPortalDetailsQuery.data?.unifiedPortalActivationDetails
          .hasActiveManagerAccount));

  // determine whether to show various banners on portal login page
  const showHasManagerAccountBanner = hasActiveManagerAccount && !hasActiveGlobalUser;
  const showActivationLinkBanner = Boolean(secureDocumentsKey);
  const showHasActiveGlobalAccountBanner =
    Boolean(activationCode) &&
    hasActiveGlobalUser &&
    !hasActiveManagerAccount &&
    Boolean(linkedGpArenaNames);
  const showContactSupportBanner =
    Boolean(activationCode) && !hasActiveGlobalUser && !hasActiveManagerAccount;

  if (user) {
    const pathParams = params.path as string;
    // redirect here uses react-router which will not allow absolute urls
    const pathname = isRedirectAllowed(pathParams, { allowAbsoluteUrls: false })
      ? pathParams
      : LP_PORTAL_BASE_PATH;
    // pass along non-path query params to location.search
    const otherSearchParams = Object.entries(params)
      // filter out path params from query params
      .filter((keyValuePair) => keyValuePair[0] !== "path")
      // format query params key-value pairs to url search format
      .map((keyValuePair) => `${keyValuePair[0]}=${formatQueryParamValue(keyValuePair[1])}`)
      .join("&");
    return (
      <Redirect
        to={{
          pathname,
          search: `?${otherSearchParams}`,
          state: location.state,
        }}
      />
    );
  }

  const splitScreen = photoUrl;

  return (
    <QueryLoader result={[response, unifiedPortalDetailsQuery]} width="100vw" height="100vh">
      <LoginLayout arena={unauthenticatedArena} splitScreen={!!splitScreen} photoUrl={photoUrl}>
        <Login
          splitScreen={!!splitScreen}
          userContext={UserContext.Global}
          onSuccess={onSuccess}
          passwordWasReset={location.state?.passwordWasReset}
          path={path}
          arena={unauthenticatedArena}
          arenaDomain={managerName === "" ? undefined : managerName}
          showPoweredBy
          showFAQ
          showArenaLoginMessage
          loginCardTitle="Sign in"
          prepopulatedUserEmail={getPrepopulatedEmail()}
          portalLinkedGpArenaNames={linkedGpArenaNames}
          showPortalTouCheckBox={showPortalTouCheckBox}
          showHasManagerAccountBanner={showHasManagerAccountBanner}
          showActivationLinkBanner={showActivationLinkBanner}
          showHasActiveGlobalAccountBanner={showHasActiveGlobalAccountBanner}
          showContactSupportBanner={showContactSupportBanner}
          secureDocumentsKey={secureDocumentsKey}
        />
      </LoginLayout>
    </QueryLoader>
  );
};
